import type { PostContentJson } from '@_/types';
import type { Node, Schema } from '@tiptap/pm/model';
import type { DocumentImageAttributes } from '../extensions/DocumentImage/index.ts';
import { DocumentImage } from '../extensions/DocumentImage/index.ts';

export abstract class BaseContent {
  protected doc: Node;

  protected abstract get schema(): Schema;

  protected constructor(content: PostContentJson) {
    this.doc = this.createDoc(content);
  }

  protected createDoc(content: PostContentJson): Node {
    return this.schema.nodeFromJSON(structuredClone(content));
  }

  equals(other: PostContentJson): boolean {
    return this.doc.eq(this.schema.nodeFromJSON(other));
  }

  getFiles(): File[] {
    const files: File[] = [];
    this.doc.forEach(node => {
      if (isDocumentImageNode(node)) {
        const file = node.attrs.file;
        if (file && !files.includes(file)) {
          files.push(file);
        }
      }
    });
    return files;
  }

  injectUploadedFileNames(uploadedFileNames: Map<File, string>): void {
    const nodes: Node[] = [];

    this.doc.forEach(node => {
      if (isDocumentImageNode(node) && !node.attrs.imageId && node.attrs.file) {
        const updatedNode = node.type.create(
          {
            ...node.attrs,
            uploadedFileName: uploadedFileNames.get(node.attrs.file)
          },
          node.children,
          node.marks
        );

        nodes.push(updatedNode);
      } else {
        nodes.push(node);
      }
    });

    this.doc = this.doc.type.create(this.doc.attrs, nodes, this.doc.marks);
  }

  serialize(): PostContentJson {
    const json = this.doc.toJSON() as PostContentJson;

    return {
      ...json,
      content: json.content
        ?.map(node => {
          if (node.type === DocumentImage.name) {
            const { imageId, uploadedFileName } = node.attrs as DocumentImageAttributes;

            if (!imageId && !uploadedFileName) {
              return;
            }

            return {
              type: node.type,
              attrs: {
                imageId: imageId || undefined,
                uploadedFileName: uploadedFileName || undefined
              }
            };
          }

          return node;
        })
        .filter(node => typeof node !== 'undefined')
    };
  }
}

function isDocumentImageNode(
  node: Node
): node is Omit<Node, 'attrs'> & { attrs: DocumentImageAttributes } {
  return node.type.name === DocumentImage.name;
}
